import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import GalleryViewLoader from 'components/common/gallery_view_loader';
import { ReactComponent as ArIcon } from 'images/icons/AR.svg';
import { trackCdp } from 'utilities/tracking';
import { localizedShopPath } from 'utilities/locales';
import { getDomain } from 'utilities/environment';
import { SEARCH_PARAM_NAMES } from 'constants/enums';
import { isMobileBrowser } from 'utilities/user_agent';
import { useTranslation } from 'react-i18next';
import {
  MarketAccessory,
  MarketButton,
  MarketButtonDropdown,
  MarketList,
  MarketRow,
} from '@market/react';
import makeStyles from '@mui/styles/makeStyles';
import {
  get3dArModelNumber,
  shouldView3dArModel,
  dropViewModelParamFromUrl,
} from 'helpers/search_parameter_helpers';
// eslint-disable-next-line import/no-unassigned-import
import '@google/model-viewer';

const TrackingEventType = {
  Click: 'Click',
  Rotate: 'Rotate',
  Scroll: 'Scroll',
  SwitchModel: 'SwitchModel',
  ArShownFromQrLink: 'ArShownFromQrLink',
  ArShownFromClick: 'ArShownFromClick',
  ArUnsupported: 'ArUnsupported',
  ArNotPresenting: 'ArNotPresenting',
  ArSessionStarted: 'ArSessionStarted',
  ArObjectPlaced: 'ArObjectPlaced',
  QrCodePresented: 'QrCodePresented',
  ArFailed: 'ArFailed',
};

const useStyles = makeStyles((theme) => ({
  arButtonContainer: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    top: '3.5%',
    width: '100%',
  },
  button: {
    backgroundColor: 'white',
    color: 'black',
    height: theme.spacing(5),
    borderRadius: '100px',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  dropDownContainer: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    bottom: '3%',
    width: '100%',
  },
  marketRow: {
    textAlign: 'center',
    userSelect: 'none',
  },
  caret: {
    verticalAlign: 'top',
  },
  background: {
    height: '100%',
    width: '100%',
    backgroundColor: '#F2F2F2',
  },
}));

export const MODEL_VIEWER_ID = 'shop-model-viewer';

const calculateDragDistance = (startCoords, endCoords) => {
  const dx = startCoords.x - endCoords.x;
  const dy = startCoords.y - endCoords.y;
  return Math.hypot(dx, dy);
};

const ThreeDimensionalView = (props) => {
  const {
    cart = {},
    models = [],
    product = {},
    setToast,
    setQrModalOpen,
    setQrCodeUrl,
  } = props;

  if (models.length === 0) {
    return null;
  }

  const { t } = useTranslation();
  const classes = useStyles();
  // The model number should always start at 1, not 0
  const modelNumber = get3dArModelNumber();
  const modelIndex =
    modelNumber && modelNumber > 0 && modelNumber <= models.length
      ? modelNumber - 1
      : 0;

  const [loaded, setLoaded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [draggingStartCoordinates, setDraggingStartCoordinates] =
    useState(null);
  const [scrollingTimeout, setScrollingTimeout] = useState(null);
  const [currentModelView, setCurrentModelView] = useState(null);
  const [currentModelIndex, setCurrentModelIndex] = useState(modelIndex);

  const trackEvent = (trackingEventType) => {
    if (
      !product ||
      Object.keys(product).length === 0 ||
      !cart ||
      Object.keys(cart).length === 0
    ) {
      return;
    }

    if (!Object.values(TrackingEventType).includes(trackingEventType)) {
      return;
    }

    trackCdp({
      name: `ThreeDimensionalView:${trackingEventType}`,
      params: {
        feature: 'shop',
        order_number: cart.number,
        // Protecting from reserve words - https://zeroheight.com/55c402603/p/069ad1-amplitude/t/42db9c
        product: {
          base_product_key: product.base_product_key,
          product_key: product.product_key,
          model: models[currentModelIndex].title,
          ...product.tracking_data,
        },
      },
    });
  };

  const clickMarketRow = (event) => {
    const index = event.target.tabIndex;
    setLoaded(false);
    setCurrentModelIndex(index);

    currentModelView.updateFraming();
    currentModelView.resetInteractionPrompt();

    trackEvent(TrackingEventType.SwitchModel);
  };

  useEffect(() => {
    const modelViewer = document.getElementById(MODEL_VIEWER_ID);
    if (!modelViewer) return;

    setCurrentModelView(modelViewer);

    modelViewer.addEventListener('load', () => {
      setLoaded(true);

      if (isMobileBrowser()) {
        if (modelViewer.canActivateAR && shouldView3dArModel()) {
          // Only wrap AR activation in try-catch - this is the operation most
          // likely to fail due to WebGL/GPU issues. Primary protection is in
          // ImageGallery via shouldRenderThreeDimensionalView().
          try {
            modelViewer.activateAR();
            trackEvent(TrackingEventType.ArShownFromQrLink);
            // We should not automatically enter phone's AR window when coming back to the AR View
            dropViewModelParamFromUrl();
          } catch {
            // AR activation failed due to WebGL/GPU issues - show unsupported message.
            trackEvent(TrackingEventType.ArUnsupported);
            setTimeout(() => {
              setToast({
                timeout: 3000,
                open: true,
                message: t('productPage.threeDimensional.arUnsupportedTitle'),
                variant: 'warning',
              });
            }, 1500);
          }
        } else if (!modelViewer.canActivateAR) {
          trackEvent(TrackingEventType.ArUnsupported);
          setTimeout(() => {
            setToast({
              timeout: 3000,
              open: true,
              message: t('productPage.threeDimensional.arUnsupportedTitle'),
              variant: 'warning',
            });
          }, 1500);
        }
      }
    });

    modelViewer.addEventListener('model-visibility', () => {
      trackEvent(TrackingEventType.Click);
    });

    modelViewer.addEventListener('ar-status', (event) => {
      switch (event.detail.status) {
        case 'not-presenting': {
          trackEvent(TrackingEventType.ArNotPresenting);
          break;
        }
        case 'session-started': {
          trackEvent(TrackingEventType.ArSessionStarted);
          break;
        }
        case 'object-placed': {
          trackEvent(TrackingEventType.ArObjectPlaced);
          break;
        }
        case 'failed': {
          trackEvent(TrackingEventType.ArFailed);
          break;
        }
        default:
        // do nothing
      }
    });
  }, []);

  const onDragStart = (event) => {
    if (!loaded) return;

    setDraggingStartCoordinates({ x: event.clientX, y: event.clientY });
    setDragging(true);
  };

  const onDragEnd = (event) => {
    if (!dragging || !loaded) return;

    const endCoordinates = { x: event.clientX, y: event.clientY };
    const distance = calculateDragDistance(
      draggingStartCoordinates,
      endCoordinates,
    );

    setDraggingStartCoordinates(null);
    setDragging(false);
    if (distance === 0) return;

    trackEvent(TrackingEventType.Rotate);
  };

  const onScroll = () => {
    if (scrollingTimeout !== null) {
      clearTimeout(scrollingTimeout);
    }

    setScrollingTimeout(
      setTimeout(() => {
        setScrollingTimeout(null);
        trackEvent(TrackingEventType.Scroll);
      }, 200),
    );
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      onPointerDown={(event) => onDragStart(event)}
      onPointerUp={(event) => onDragEnd(event)}
      onWheel={() => onScroll()}
      className={classes.background}
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
      }}
    >
      {!loaded && <GalleryViewLoader />}
      <model-viewer
        ar
        ar-placement={models[currentModelIndex].placement?.toLowerCase()}
        ar-modes="webxr scene-viewer quick-look"
        ar-scale="fixed"
        id={MODEL_VIEWER_ID}
        camera-controls
        loading="lazy"
        orientation={models[currentModelIndex].orientation?.toLowerCase()}
        interaction-prompt="auto"
        interaction-prompt-style="wiggle"
        interaction-prompt-threshold="200"
        exposure="1.5"
        shadow-intensity="1"
        src={models[currentModelIndex].url}
        style={{
          height: loaded ? '100%' : '0%', // allows loader to be in center
          width: loaded ? '100%' : '0%', // allows loader to be in center
        }}
      >
        {/* Hide default progress bar for custom one */}
        <div slot="progress-bar" />
        {/* Hide default ar button for custom one */}
        <div slot="ar-button" />
        <div slot="poster" className={classes.background} />

        <div className={classes.arButtonContainer}>
          {isMobileBrowser() ? (
            currentModelView?.canActivateAR ? (
              <MarketButton
                className={classes.button}
                size="small"
                onClick={() => {
                  currentModelView.activateAR();
                  trackEvent(TrackingEventType.ArShownFromClick);
                }}
              >
                <MarketAccessory slot="icon">
                  <ArIcon />
                </MarketAccessory>
                {t('productPage.threeDimensional.viewInSpaceTitle')}
              </MarketButton>
            ) : null
          ) : (
            <MarketButton
              className={classes.button}
              size="small"
              onClick={() => {
                // The model number should always start at 1, not 0
                setQrCodeUrl(
                  `https://${getDomain('')}${localizedShopPath()}/products/${
                    product.slug
                  }?${SEARCH_PARAM_NAMES.MODEL_VIEW}=true&${
                    SEARCH_PARAM_NAMES.MODEL_NUMBER
                  }=${currentModelIndex + 1}`,
                );
                setQrModalOpen(true);

                trackEvent(TrackingEventType.QrCodePresented);
              }}
            >
              <MarketAccessory slot="icon">
                <ArIcon />
              </MarketAccessory>
              {t('productPage.threeDimensional.viewInSpaceTitle')}
            </MarketButton>
          )}
        </div>

        {models.length > 1 && (
          <div className={classes.dropDownContainer}>
            <MarketButtonDropdown popover-placement="top">
              <MarketButton
                className={classes.button}
                size="small"
                slot="trigger"
              >
                {`${models[currentModelIndex].display_title}`}
              </MarketButton>
              <MarketList transient="true" slot="content">
                {models.map((_model, index) => (
                  <MarketRow
                    key={index}
                    className={classes.marketRow}
                    tabIndex={index}
                    size="small"
                    onClick={(event) => clickMarketRow(event)}
                    {...(index === currentModelIndex
                      ? { disabled: 'true' }
                      : {})}
                  >
                    {models[index].display_title}
                  </MarketRow>
                ))}
              </MarketList>
            </MarketButtonDropdown>
          </div>
        )}
      </model-viewer>
    </Box>
  );
};

export default ThreeDimensionalView;
